import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

//* Importing List and Loading components
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Importing useQuestions hook
import { useQuestions } from "../../../hooks/questions/useQuestions";

//* Importing ModalManage and ModalWarning
import ModalManage from "./components/ModalManage";
import { ModalWarning } from "../../../component/common/ModalWarning";

//! Importing API
import { API_URI } from "../../../constants/consts";
import { api } from "../../../services/apis/configs/axiosConfigs";

//* Importing useDisciplines hook
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";

//* Funções de mapeamento
import {
  mapEducationLevel,
  mapDifficultyLevel,
} from "../../../utils/questions/questionsMap";

//* Import Bootstrap components
import { Dropdown } from "react-bootstrap";

var debounce = require("lodash.debounce");

export function QuestionsView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [showWarning, setShowWarning] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const [showModalManage, setShowModalManage] = useState<boolean>(false);
  const [showWarningErroModal, setShowWarningErroModal] =
    useState<boolean>(false);
  const [modalQuestionId, setModalQuestionId] = useState<string | null>(null);

  // Hook principal das questões
  const {
    questions,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    refetch,
    changePage,
    isDeleting,
    updateFilters,
    filters,
  } = useQuestions(token);

  // Hook das disciplinas
  const { discipline } = useDisciplines(token);

  
  const deleteQuestionCustom = useCallback(
    async (id: string): Promise<void> => {
      const url = `${API_URI}/admin/private/questions/${id}`;

      try {
        await api.request({
          url,
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        refetch();
      } catch (error: any) {
        console.error("Erro ao deletar questão:", error);

        if (error?.response?.status === 409) {
          const conflictError = new Error("CONFLICT");
          (conflictError as any).status = 409;
          throw conflictError;
        }

        throw error;
      }
    },
    [token]
  );



  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (questionToDelete) {
      try {
        const currentPageItems = questions.length;
        const currentPage = pagination.currentPage;

        await deleteQuestionCustom(questionToDelete);

        // Se só havia 1 item na página atual, volta para página anterior
        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setQuestionToDelete(null);
      } catch (error: any) {
        console.error("Erro ao deletar questão:", error);

        // Se for 409, exibe modal de aviso
        if (error.status === 409) {
          setShowWarning(false);
          setQuestionToDelete(null);
          setShowWarningErroModal(true);
        } else {
          setShowWarning(false);
          setQuestionToDelete(null);
          setShowWarningErroModal(true);
        }
      }
    }
  }, [
    questionToDelete,
    deleteQuestionCustom,
    questions.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setQuestionToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  // Filtro de disciplina
  const handleDisciplineChange = useCallback(
    (newDisciplineId: string | undefined) => {
      updateFilters({
        disciplineId: newDisciplineId,
      });
    },
    [updateFilters]
  );

  // dropdown de disciplinas
  const filterComponents = (
    <div className="d-flex align-items-center flex-wrap">
      <Dropdown className="d-flex align-items-center ms-2">
        <Dropdown.Toggle variant="success" id="dropdown-discipline">
          {filters.disciplineId
            ? discipline.find((d: any) => d.id === filters.disciplineId)?.name
            : "Todas as Disciplinas"}
        </Dropdown.Toggle>
        <Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
          <Dropdown.Item onClick={() => handleDisciplineChange(undefined)}>
            Todas as Disciplinas
          </Dropdown.Item>
          {discipline.map((d: any) => (
            <Dropdown.Item
              key={d.id}
              onClick={() => handleDisciplineChange(d.id)}
            >
              {d.name}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );

  const transformedItems = questions.map((question) => ({
    ...question,
    key: question.id,
    educationLevel: mapEducationLevel(question.educationLevel),
    difficultyLevel: mapDifficultyLevel(question.difficultyLevel),
    disciplineName: question.discipline.name,
  }));

  const handleManagerActionClick = useCallback((id: string) => {
    setModalQuestionId(id);
    setShowModalManage(true);
  }, []);

  const handleCloseModalManage = useCallback(() => {
    setShowModalManage(false);
    setModalQuestionId(null);
  }, []);

  if (isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Erro: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        search_text="Pesquise pelo enunciado da questão"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        title={`Questões`}
        not_found_message="Nenhuma questão encontrada"
        button_link="/questions/create"
        button_text="Adicionar questão"
        deleting_message="Ao deletar esta questão, ela não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/questions/edit/${id}`)}
        onChangePagination={handleChangePagination}
        onManagerActionClick={handleManagerActionClick}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={transformedItems}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          {
            name: "educationLevel",
            type: "text",
            header: "Nível de Educação",
          },
          {
            name: "difficultyLevel",
            type: "text",
            header: "Nível de Dificuldade",
          },
          { name: "disciplineName", type: "text", header: "Disciplina" },
          { name: "createdBy", type: "text", header: "Criado por" },
          { name: "createdAt", type: "date", header: "Data de Criação" },
        ]}
        loading_search={isSearching}
        additionalFilters={filterComponents}
      />

      {/* Modal de gerenciamento */}
      <ModalManage
        id={modalQuestionId}
        token={token}
        showModalManage={showModalManage}
        setShowModalManage={handleCloseModalManage}
      />

      {/* Modal de Aviso (Erro de Exclusão, 409, etc.) */}
      <ModalWarning
        show={showWarningErroModal}
        onClose={() => setShowWarningErroModal(false)}
        msg="Essa Questão está sendo usada em uma prova, portanto não pode ser deletada."
        title="Aviso"
      />
    </div>
  );
}
